import type { MouseEventHandler } from 'react'
import { Link } from 'react-router-dom'

import { withReturnTo } from '@/lib/return-navigation'
import { withServiceInstanceId } from '@/lib/service-instance'
import { cn } from '@/lib/utils'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'

type JobTitleValue = string | number | null | undefined

type JobTitleLinkProps = {
  jobId?: JobTitleValue
  title?: JobTitleValue
  jobRef?: JobTitleValue
  entityLabel?: string
  detailBasePath?: string
  serviceInstanceId?: string | null
  returnPath?: string | null
  className?: string
  onClick?: MouseEventHandler<HTMLAnchorElement>
}

const normalizedText = (value: JobTitleValue) => String(value ?? '').trim()

export function formatJobTitleWithId({
  jobId,
  title,
  jobRef,
  entityLabel = 'Job',
}: {
  jobId?: JobTitleValue
  title?: JobTitleValue
  jobRef?: JobTitleValue
  entityLabel?: string
}) {
  const id = normalizedText(jobId)
  const text = normalizedText(title)
  const ref = normalizedText(jobRef)
  const base = text || (ref ? `${entityLabel} ${ref}` : '')

  if (!id) {
    return base || `Unknown ${entityLabel.toLowerCase()}`
  }

  const idLabel = `${entityLabel} #${id}`
  if (!base) {
    return idLabel
  }

  const lowerBase = base.toLowerCase()
  if (base.includes(`#${id}`) || lowerBase.includes(`${entityLabel.toLowerCase()} #${id}`)) {
    return base
  }

  return `${base} · ${idLabel}`
}

export function getJobDetailPath(
  jobId: JobTitleValue,
  serviceInstanceId?: string | null,
  returnPath?: string | null,
  detailBasePath = '/job',
) {
  const id = normalizedText(jobId)
  if (!id) {
    return null
  }

  return withReturnTo(
    withServiceInstanceId(`${detailBasePath}/${encodeURIComponent(id)}`, serviceInstanceId),
    returnPath,
  )
}

export function JobTitleLink({
  jobId,
  title,
  jobRef,
  entityLabel: entityLabelProp,
  detailBasePath: detailBasePathProp,
  serviceInstanceId,
  returnPath,
  className,
  onClick,
}: JobTitleLinkProps) {
  const labels = useWorkspaceLabels()
  const entityLabel = entityLabelProp ?? labels.entity
  const detailBasePath = detailBasePathProp ?? labels.basePath
  const label = formatJobTitleWithId({ jobId, title, jobRef, entityLabel })
  const href = getJobDetailPath(jobId, serviceInstanceId, returnPath, detailBasePath)

  if (!href) {
    return <span className={className}>{label}</span>
  }

  return (
    <Link
      to={href}
      className={cn('text-primary hover:underline', className)}
      onClick={onClick}
    >
      {label}
    </Link>
  )
}
